import DB from '../../private/db';
import tableConstructor from './table';
import type {
    IModelInstantiator,
    INSTANTIATOR_INTERFACES,
} from '../../model/interface/instantiator';
import { IDBOptions } from '../interface/instantiator';
import { IWorkspaceDB } from '../interface/db';
import { IWorkspaceTable } from '../interface/table';
import migration from '../private/migration';
import type { DataMap } from '../private/migration/interface';

/* Api data from serve response for type of Interface */
type ApiData<Interface extends keyof INSTANTIATOR_INTERFACES> = Parameters<
    INSTANTIATOR_INTERFACES[Interface]
>[0];

class ContainerDB implements IWorkspaceDB {
    private _db: DB;
    private _openCount = 0;
    private _modelInstantiator: IModelInstantiator;

    name = '';

    constructor(modelInstantiator: IModelInstantiator, options: IDBOptions = {}) {
        const dbMigrator = migration.versions[migration.versions.length - 1];

        this._db = new DB({
            version: dbMigrator?.version,
            mappings: dbMigrator?.mappings,
            name: options.name ?? 'Workspace',
            indexedDB: options.indexedDB,
            IDBKeyRange: options.IDBKeyRange,
        });

        /* pass in migrators to handle version upgrades */
        for (const migrator of migration.versions) {
            this._db.version(migrator.version).upgrade(migrator.upgrade);
        }

        this._modelInstantiator = modelInstantiator;
        this.name = this._db.name;
    }

    open(): Promise<ContainerDB> {
        this._openCount += 1;
        if (this._openCount <= 1) {
            const self = this;
            return new Promise((resolve, reject) => {
                this._db
                    .open()
                    .then(_ => resolve(self))
                    .catch(err => reject(err));
            });
        } else {
            return Promise.resolve(this);
        }
    }

    close(): void {
        if (this.isOpen() && this._openCount <= 1) {
            this._db.close();
        }

        this._openCount = Math.max(0, this._openCount - 1);
    }

    isOpen(): boolean {
        return this._db.isOpen();
    }

    preInsertCleanup(dataIn: DataMap): DataMap {
        let dataOut = dataIn;

        for (const migrator of migration.versions) {
            dataOut = migrator.preInsertCleanup(dataOut);
        }

        return dataOut;
    }

    table<
        Interface extends keyof INSTANTIATOR_INTERFACES,
        Entity extends ReturnType<INSTANTIATOR_INTERFACES[Interface]>,
    >(
        tableName: Interface,
    ): IWorkspaceTable<Interface, INSTANTIATOR_INTERFACES[Interface], Entity> {
        const table = this._db.table(tableName);
        return tableConstructor(tableName, table);
    }

    first<
        Interface extends keyof INSTANTIATOR_INTERFACES,
        Entity extends ReturnType<INSTANTIATOR_INTERFACES[Interface]>,
    >(tableName: Interface): Promise<Entity> {
        if (!tableName) {
            return Promise.reject('No Table to search with');
        }

        const db = this._db;
        const instantiator = this._modelInstantiator.instantiate;

        return new Promise<Entity>(function (resolve, reject) {
            db.table(tableName)
                .toArray()
                .then((items: Array<ApiData<Interface>>) => {
                    if (items.length > 0) {
                        const obj = instantiator(tableName, items[0]);
                        resolve(obj as Entity);
                    } else {
                        reject(new Error(`No items found in table: ${tableName}`));
                    }
                })
                .catch((err: Error) => reject(err));
        });
    }

    get<
        Interface extends keyof INSTANTIATOR_INTERFACES,
        Entity extends ReturnType<INSTANTIATOR_INTERFACES[Interface]>,
    >(tableName: Interface, pk: any): Promise<Entity> {
        if (!pk) {
            return Promise.reject(`No PK to search with, table:${tableName}, pk:${pk}`);
        }

        const db = this._db;
        const instantiator = this._modelInstantiator.instantiate;

        return new Promise<Entity>(function (resolve, reject) {
            db.table(tableName)
                .get(pk)
                .then((data?: ApiData<Interface>) => {
                    if (data) {
                        const item = instantiator(tableName, data);
                        resolve(item as Entity);
                    } else {
                        reject(`No data found in ${tableName} matching pk:${pk}`);
                    }
                })
                .catch((err: Error) => reject(err));
        });
    }

    all<
        Interface extends keyof INSTANTIATOR_INTERFACES,
        Entity extends ReturnType<INSTANTIATOR_INTERFACES[Interface]>,
    >(tableName: Interface): Promise<Array<Entity>> {
        if (!tableName) {
            return Promise.reject('No Table to search with');
        }

        const db = this._db;
        const instantiator = this._modelInstantiator.instantiate;

        return new Promise<Array<Entity>>(function (resolve, reject) {
            db.table(tableName)
                .toArray()
                .then((itemDatas: Array<ApiData<Interface>>) => {
                    const items: Entity[] = itemDatas.map(item => instantiator(tableName, item));
                    resolve(items);
                })
                .catch((err: Error) => reject(err));
        });
    }
}

export default ContainerDB;
